import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  TablePaginatedV8,
  EditCellV8,
  TableCellV8,
  SelectColumnV8,
  TableEditButtons,
  ButtonNewRecord,
  promiseModalDialog,
  toastSuccess,
} from 'motion-components';
import { v4 as uuidv4 } from 'uuid';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { useMutation } from '@apollo/client';

import { useRootStore } from 'store';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import {
  useGetMaterialsAsOptionsQuery,
  promiseModalSelectMultiMaterial,
  useGetJobTypesAsOptionsQuery,
} from 'shared-pages/components';
import { gqlDeleteCollectionLine } from 'shared-pages/pages/collection-orders/graphql';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { isFotokiklosi } from 'shared-pages/common/constants';
import { useGetMaterialById } from 'shared-pages/common/db-help-func';

export interface ICollectionLine {
  id: number | null;
  aa: number;
  tempId?: string; // NEW 🔥
  materialId: number | null;
  qty: number;
  netWeight: number;
  jobtypeId?: number | null;
  material?: {
    id: number | null;
    name: string;
  };
  jobtype?: {
    id: number | null;
    description: string;
  };
  finDispNote?: string;
}

interface CollectionExtrasTableProps {
  isNew: boolean;
  canEdit: boolean;
}
export const createOnChangeMaterial = (values, setFieldValue) => {
  return (value, row) => {
    const rowIndex = row.index;
    const materialName = row?.valueName ?? '';

    if (!values?.collectionLines || !values?.collectionLines[rowIndex]) {
      return;
    }

    setFieldValue(`collectionLines[${rowIndex}]`, {
      ...values?.collectionLines[rowIndex],
      materialId: value,
      material: {
        id: value,
        name: materialName,
      },
    });
  };
};

export const createOnChangeJobType = (values, setFieldValue) => {
  return (value, row) => {
    const rowIndex = row.index;

    if (!values?.collectionLines || !values?.collectionLines[rowIndex]) {
      return;
    }

    setFieldValue(`collectionLines[${rowIndex}]`, {
      ...values?.collectionLines[rowIndex],
      jobtypeId: value,
    });
  };
};

const CollectionExtrasTable = ({ isNew, canEdit }: CollectionExtrasTableProps) => {
  const { t } = useTranslation();
  const location = useLocation<any>();
  const history = useHistory();

  const { appStore, tableStore } = useRootStore();
  const { canAddNew } = useUserLocationPermissions();

  // For row selection or highlighting
  const [selected, setSelected] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // 🏆 Single source of truth: read + write `collectionLines` from Formik
  const { values, setFieldValue, errors } = useFormikContext<{
    collectionLines: ICollectionLine[];
  }>();
  const gridRef = useRef(null);

  // For immediate “delete” calls
  const [deleteContainer] = useMutation(gqlDeleteCollectionLine);
  //useEffect to wait for the instantiation of the formik values

  // -- Add Materials Using a Modal --
  const handleAddMaterials = async () => {
    try {
      setLoading(true);

      // If using Fotokiklosi, pass existing IDs
      const selectedMaterialIds = isFotokiklosi
        ? values?.collectionLines?.map((item) => item.materialId).join(',')
        : '';

      const selectedMaterials = await promiseModalSelectMultiMaterial({
        appStore,
        tableStore,
        location,
        idValues: selectedMaterialIds,
        initialIdValues: selectedMaterialIds,
        columns: [
          SelectColumnV8(),
          {
            id: 'name',
            header: 'Απόβλητα',
            columnName: 'Απόβλητα',
            accessorKey: 'name',
            orderByField: 'name',
            cell: TableCellV8,
            size: 120,
          },
        ],
      });

      // user may cancel
      if (!selectedMaterials) return;

      // Filter out duplicates
      const newMaterials = selectedMaterials
        .filter((mat) => !values.collectionLines.some((line) => line.materialId === mat.id))
        .map((material, index) => ({
          id: null,
          tempId: uuidv4(), // NEW 🔥
          aa: values.collectionLines.length + 1 + index,
          materialId: material.id,
          qty: 0,
          netWeight: 0,
          finDispNote: '',
          jobtypeId: null,
          material: {
            id: material.id,
            name: material.name,
          },
          jobtype: { id: null, description: '' },
        }));
      //filter out the ones

      if (newMaterials.length > 0) {
        setFieldValue('collectionLines', [...values.collectionLines, ...newMaterials]);
      } else {
        console.log('No new materials added, or all were selected already.');
      }
    } catch (error) {
      console.error('Error selecting materials:', error);
    } finally {
      setLoading(false);
    }
  };
  const onChangeMaterial = useCallback(
    (value, row) => {
      const rowIndex = row.index;
      const materialName = row?.valueName ?? '';

      setFieldValue(`collectionLines[${rowIndex}]`, {
        ...row.original,
        materialId: value,
        material: {
          id: value,
          name: materialName,
        },
      });
    },
    [values, setFieldValue],
  );

  const onChangeJobType = useCallback(
    (value, row) => {
      const rowIndex = row.index;
      const jobtypeDescription = row?.valueName ?? '';

      setFieldValue(`collectionLines[${rowIndex}]`, {
        ...row.original,
        jobtypeId: value,
        jobtype: {
          id: value,
          description: jobtypeDescription,
        },
      });
    },
    [values, setFieldValue],
  );

  // Column definitions

  const columns = useMemo(() => {
    return [
      {
        id: 'aa',
        header: t('Αα'),
        accessorKey: 'aa',
        cell: TableCellV8,
        meta: { type: 'number' },
        size: 10,
      },
      {
        id: 'materialId',
        header: t('Απόβλητο'),
        accessorKey: 'materialId',
        cell: TableCellV8,
        size: 30,
        meta: {
          type: 'select',
          idKey: 'materialId',
          idName: 'material.name',
          hook: useGetMaterialsAsOptionsQuery,
          onValueChange: onChangeMaterial,
          where: JSON.stringify({
            isActive: 1,
            useInProduction: 0,
          }),
        },
      },

      {
        id: 'qty',
        header: t('Ποσότητα'),
        accessorKey: 'qty',
        cell: TableCellV8,
        meta: { type: 'number' },
        size: 10,
      },
      {
        id: 'netWeight',
        header: t('Βάρος'),
        accessorKey: 'netWeight',
        cell: TableCellV8,
        meta: { type: 'number' },
        size: 10,
      },
      {
        id: 'finDispNote',
        header: t('Δελτίο Αποστολής'),
        accessorKey: 'finDispNote',
        cell: TableCellV8,
        meta: { type: 'text', nullable: true },
        size: 20,
      },
      ...(isFotokiklosi
        ? [
            {
              id: 'jobtypeId',
              header: t('Τύπος Εργασίας'),
              accessorKey: 'jobtypeId',
              cell: TableCellV8,
              size: 25,
              meta: {
                type: 'select',
                idKey: 'jobtypeId',
                idName: 'jobtype.description', // Show the description of the jobtype from nested object
                hook: useGetJobTypesAsOptionsQuery,
                onValueChange: onChangeJobType,
                where: JSON.stringify({
                  isActive: true,
                }),
              },
            },
          ]
        : []),
      {
        id: 'edit',
        header: t('Ενέργειες'),
        accessorKey: 'edit',
        size: 20,
        cell: (props: any) =>
          canEdit ? EditCellV8({ row: props.row, table: props.table }) : <></>,
      },
    ];
  }, [t, onChangeMaterial, onChangeJobType]);

  const { getMaterialById } = useGetMaterialById();
  // Called when user clicks Save in row-edit mode
  const onSaveRow = async (rowIndex: number, row: any, changes: any) => {
    setLoading(true);
    try {
      const lines = [...values?.collectionLines];
      const existingLine = lines[rowIndex];

      // If new row => add it to Formik
      if (!existingLine) {
        // create a new row object
        const newLine: ICollectionLine = {
          id: null,
          aa: lines.length + 1,
          materialId: changes.materialId ?? null,
          qty: changes.qty ? parseInt(changes.qty, 10) : 0,
          netWeight: changes.netWeight ? parseFloat(changes.netWeight) : 0,
          finDispNote: changes.finDispNote || '',
          jobtypeId: changes.jobtypeId ?? null,
        };
        lines.push(newLine);
      } else {
        // update existing row
        console.log('$$% onSaveRow (Existing) changes:', changes, existingLine);

        lines[rowIndex] = {
          ...existingLine,
          ...changes,
          qty: changes?.qty ? parseInt(changes.qty, 10) : existingLine.qty,
          netWeight: changes.netWeight ? parseFloat(changes.netWeight) : existingLine.netWeight,
        };
      }
      setFieldValue('collectionLines', lines);
    } catch (err) {
      console.error('Error in onSaveRow:', err);
    } finally {
      setLoading(false);
    }
    return true;
  };

  // Delete row => remove from Formik. If row.id => call server to delete immediately
  const handleDeleteRow = async (row: any) => {
    // Use row directly as your line object
    const lineObj = row;
    if (!lineObj) return false;

    // If new (undefined or null `id`):
    if (!lineObj.id) {
      const lines = [...values.collectionLines];
      const idx = lines.findIndex((l) =>
        l.id === null && l.tempId
          ? l.tempId === lineObj.tempId // NEW ✅
          : l.id === lineObj.id && l.aa === lineObj.aa,
      );

      if (idx > -1) lines.splice(idx, 1);
      setFieldValue('collectionLines', lines);
      return true;
    }

    // Otherwise, confirm + delete on the server
    const answer = await promiseModalDialog({
      title: t('Διαγραφή Εγγραφής'),
      text: `Θέλετε να διαγράψετε το Απόβλητο "${lineObj?.material?.name}" ?`,
    });
    if (!answer) return false;

    try {
      await deleteContainer({ variables: { id: lineObj.id } });
      toastSuccess(t('Διαγράφηκε το Απόβλητο'));
    } catch (err) {
      console.error(err);
      return false;
    }

    // Remove from form
    const lines = [...values.collectionLines];
    const idx = lines.findIndex(
      (l) => (l.id ? l.id === lineObj.id : l.tempId === lineObj.tempId), // NEW ✅
    );
    if (idx > -1) lines.splice(idx, 1);
    setFieldValue('collectionLines', lines);

    return true;
  };
  // The lines we render in the table come directly from Formik
  const linesData = values?.collectionLines;

  return (
    <LoadingOverlay active={loading} spinner text={t('Παρακαλώ περιμένετε...')}>
      <div
        className="form-group-title d-flex justify-content-between align-items-center overflow-y-hidden"
        style={{ marginBottom: '0px' }}
      >
        <h5 style={{ marginBottom: '0px' }}>{t('Απόβλητα Συλλογής')}</h5>
        <div className="d-flex">
          <ButtonNewRecord
            className="align-self-center me-3"
            label={t('Προσθήκη Αποβλήτων')}
            onClick={handleAddMaterials}
            disabled={!canEdit}
          />
          <TableEditButtons
            className="me-3"
            canEdit={canEdit}
            canInsert={canEdit}
            canDelete={canEdit}
            tableOptionsMeta={() => gridRef.current?.table.options.meta}
            twoLines={false}
          />
        </div>
      </div>

      <TablePaginatedV8
        style={{ overflow: 'visible' }}
        rowProps={(row: any) => {
          const lineErr = errors.collectionLines?.[row.index];
          let style = undefined;

          const isSelected =
            (row.original.id && row.original.id === selected?.id) ||
            (!row.original.id && row.original.tempId && row.original.tempId === selected?.tempId); // NEW ✅

          if (isSelected) style = gridRowSelectedStyle;
          if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
          if (lineErr) style = { ...style, color: 'red' };

          return { style };
        }}
        ref={gridRef}
        loading={false}
        id="collection-lines-grid"
        appStore={appStore}
        tableStore={tableStore}
        key="collection-lines-grid"
        columns={columns as any}
        onRowClick={(props) => setSelected(props.row.original)}
        onSaveRow={onSaveRow}
        deleteRow={handleDeleteRow}
        onNewRow={() => {
          // If user clicks a table-based insert (instead of ButtonNewRecord):
          return {
            id: null,
            aa: linesData.length + 1,
            tempId: uuidv4(),
            materialId: null,
            qty: 0,
            netWeight: 0,
            material: { id: null, name: '' },
            finDispNote: '',
            jobtypeId: null,
            jobtype: { id: null, description: '' },
          } as ICollectionLine;
        }}
        data={linesData ?? []}
        dataCount={linesData?.length ?? 0}
        topBorderRadius={false}
        initSorting={[{ id: 'material.name', desc: true }]}
      />

      {/* If there are errors, display them */}
      {errors?.collectionLines && typeof errors.collectionLines === 'string' && (
        <div style={{ color: 'red', marginTop: '10px' }}>
          <strong>{errors.collectionLines}</strong>
        </div>
      )}
      {errors?.collectionLines &&
        Array.isArray(errors.collectionLines) &&
        errors.collectionLines.length > 0 && (
          <div style={{ color: 'red', marginTop: '10px' }}>
            <strong>{t('Υπάρχουν λάθη στις γραμμές του πίνακα')}:</strong>
            <ul>
              {errors.collectionLines.map((lineError, index) => {
                if (!lineError) return null;
                const messages =
                  typeof lineError === 'object' ? Object.values(lineError).join(', ') : lineError;
                return (
                  <li key={index}>
                    <strong>
                      {t('Σειρά')} {index + 1}:
                    </strong>{' '}
                    {messages}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
    </LoadingOverlay>
  );
};

export default CollectionExtrasTable;
